import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Heart, X, Calendar, ExternalLink, MapPin, Bed, Bath, Square,
  Loader2, Home, CheckCircle, MessageSquare, ChevronLeft, ChevronRight,
  Eye, Send, HelpCircle, ThumbsDown, Sparkles
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import PortalLayout from './PortalLayout';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription
} from '../../components/ui/sheet';
import { ScrollArea } from '../../components/ui/scroll-area';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REACTIONS = [
  { key: 'love_it', label: 'Love it', icon: Heart, activeClass: 'bg-rose-500/20 text-rose-400 border-rose-500/30' },
  { key: 'interested', label: 'Interested', icon: Sparkles, activeClass: 'bg-[#2CA8A0]/20 text-[#2CA8A0] border-[#2CA8A0]/30' },
  { key: 'need_more_info', label: 'More info', icon: HelpCircle, activeClass: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  { key: 'not_for_me', label: 'Not for me', icon: ThumbsDown, activeClass: 'bg-[#555]/20 text-[#888] border-[#555]/30' },
];

const formatPrice = (amount, currency, intent) => {
  if (!amount) return '';
  const formatted = `${currency === 'MXN' ? 'MX$' : '$'}${amount.toLocaleString()}`;
  return intent === 'rent' ? `${formatted}/mo` : formatted;
};

// Image with fallback
const ListingImage = ({ src, alt, className }) => {
  const [error, setError] = useState(false);
  if (!src || error) {
    return (
      <div className={`${className} flex items-center justify-center bg-[#111]`}>
        <Home className="w-12 h-12 text-[#333]" />
      </div>
    );
  }
  return <img src={src} alt={alt} className={className} onError={() => setError(true)} />;
};

// Side Panel
const ListingDetailPanel = ({ open, item, items, currentIndex, onClose, onNavigate, onReaction, onSaveNote, reactingTo, savingNote }) => {
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [noteText, setNoteText] = useState('');
  const [touchStart, setTouchStart] = useState(null);

  useEffect(() => {
    setActiveImageIndex(0);
    setNoteText(item?.client_note || '');
  }, [item?.id]);

  const handleSwipe = (direction) => {
    if (!item?.images?.length) return;
    if (direction === 'left') setActiveImageIndex(i => Math.min(item.images.length - 1, i + 1));
    if (direction === 'right') setActiveImageIndex(i => Math.max(0, i - 1));
  };

  const hasNext = currentIndex < items.length - 1;
  const hasPrev = currentIndex > 0;

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-none sm:w-[55vw] bg-[#F9F6EF] border-l border-[#FFFFFF] p-0 overflow-hidden [&>button]:hidden"
        data-testid="listing-detail-panel"
      >
        <SheetHeader className="sr-only">
          <SheetTitle>{item?.title || 'Listing Detail'}</SheetTitle>
          <SheetDescription>Property listing details</SheetDescription>
        </SheetHeader>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#FFFFFF] bg-[#F9F6EF]">
          <div className="flex items-center gap-3">
            <button
              onClick={() => onNavigate('prev')}
              disabled={!hasPrev}
              data-testid="panel-prev-btn"
              className={`p-1.5 transition ${hasPrev ? 'text-white hover:bg-[#FFFFFF]' : 'text-[#333] cursor-not-allowed'}`}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-[#666] text-sm tabular-nums">{currentIndex + 1} / {items.length}</span>
            <button
              onClick={() => onNavigate('next')}
              disabled={!hasNext}
              data-testid="panel-next-btn"
              className={`p-1.5 transition ${hasNext ? 'text-white hover:bg-[#FFFFFF]' : 'text-[#333] cursor-not-allowed'}`}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-[#666] hover:text-white transition"
            data-testid="panel-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {item && (
          <ScrollArea className="h-[calc(100vh-53px)]">
            {/* Photo Gallery */}
            <div
              className="relative bg-[#111]"
              onTouchStart={(e) => setTouchStart(e.touches[0].clientX)}
              onTouchEnd={(e) => {
                if (touchStart === null) return;
                const diff = touchStart - e.changedTouches[0].clientX;
                if (Math.abs(diff) > 50) handleSwipe(diff > 0 ? 'left' : 'right');
                setTouchStart(null);
              }}
            >
              <div className="aspect-[16/9]">
                <ListingImage
                  src={item.images?.[activeImageIndex] || item.images?.[0]}
                  alt={item.title}
                  className="w-full h-full object-cover"
                />
              </div>

              {item.images?.length > 0 && (
                <div className="absolute top-3 left-3 bg-black/70 px-2.5 py-1 text-xs text-white">
                  {activeImageIndex + 1} / {item.images.length}
                </div>
              )}

              <div className="absolute top-3 right-3">
                <span className={`px-2.5 py-1 text-xs font-medium ${
                  item.intent === 'rent' ? 'bg-blue-500 text-white' : 'bg-green-500 text-white'
                }`}>
                  {item.intent === 'rent' ? 'For Rent' : 'For Sale'}
                </span>
              </div>

              {item.images?.length > 1 && (
                <>
                  <button
                    onClick={() => setActiveImageIndex(i => Math.max(0, i - 1))}
                    className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 bg-black/60 text-white hover:bg-black/80 transition hidden sm:block"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setActiveImageIndex(i => Math.min((item.images?.length || 1) - 1, i + 1))}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-black/60 text-white hover:bg-black/80 transition hidden sm:block"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>

            {/* Thumbnails */}
            {item.images?.length > 1 && (
              <div className="flex gap-1.5 px-5 py-3 overflow-x-auto bg-[#F9F6EF]">
                {item.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImageIndex(idx)}
                    className={`w-16 h-12 shrink-0 overflow-hidden border-2 transition ${
                      idx === activeImageIndex ? 'border-[#2CA8A0]' : 'border-transparent opacity-50 hover:opacity-100'
                    }`}
                    data-testid={`panel-thumb-${idx}`}
                  >
                    <ListingImage src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Details */}
            <div className="px-5 py-5 space-y-5">
              <div>
                <h2 className="text-xl text-white font-medium mb-1" data-testid="panel-title">{item.title}</h2>
                <p className="text-xl text-[#2CA8A0] font-semibold" data-testid="panel-price">
                  {formatPrice(item.price_amount, item.price_currency, item.intent)}
                </p>
              </div>

              {(item.neighborhood || item.city) && (
                <div className="flex items-center gap-2 text-[#888]">
                  <MapPin className="w-4 h-4 shrink-0" />
                  <span>{typeof item.neighborhood === 'object' ? (item.neighborhood?.name || '') : item.neighborhood ? `${item.neighborhood}, ` : ''}{typeof item.city === 'object' ? (item.city?.name || '') : item.city}</span>
                </div>
              )}

              {/* Specs */}
              <div className="grid grid-cols-3 gap-3 p-4 bg-[#F9F6EF] border border-[#FFFFFF]">
                <div className="text-center">
                  <Bed className="w-5 h-5 text-[#2CA8A0] mx-auto mb-1" />
                  <p className="text-white font-medium">{item.bedrooms || '-'}</p>
                  <p className="text-xs text-[#666]">Beds</p>
                </div>
                <div className="text-center">
                  <Bath className="w-5 h-5 text-[#2CA8A0] mx-auto mb-1" />
                  <p className="text-white font-medium">{item.bathrooms || '-'}</p>
                  <p className="text-xs text-[#666]">Baths</p>
                </div>
                <div className="text-center">
                  <Square className="w-5 h-5 text-[#2CA8A0] mx-auto mb-1" />
                  <p className="text-white font-medium">{item.area_sqm || '-'}</p>
                  <p className="text-xs text-[#666]">m²</p>
                </div>
              </div>

              {/* Mundalo Notes */}
              {item.frg_notes && (
                <div className="p-4 bg-[#2CA8A0]/10 border border-[#2CA8A0]/30">
                  <div className="flex items-center gap-2 text-[#2CA8A0] text-sm mb-2">
                    <MessageSquare className="w-4 h-4" />
                    <span className="font-medium">From your advisor</span>
                  </div>
                  <p className="text-[#1A5C6E] text-sm leading-relaxed">{item.frg_notes}</p>
                </div>
              )}

              {/* Description */}
              {item.description && (
                <div>
                  <h3 className="text-xs text-[#666] uppercase tracking-wider mb-2">Description</h3>
                  <p className="text-[#888] text-sm leading-relaxed">{item.description}</p>
                </div>
              )}

              {/* Source Link */}
              {item.source_url && (
                <a
                  href={item.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-[#888] hover:text-[#2CA8A0] transition"
                  data-testid="panel-source-link"
                >
                  <ExternalLink className="w-4 h-4" />
                  View Original Listing
                </a>
              )}

              {/* Reactions */}
              <div className="border-t border-[#FFFFFF] pt-5">
                <p className="text-xs text-[#666] uppercase tracking-wider mb-3">Your reaction</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {REACTIONS.map(({ key, label, icon: Icon, activeClass }) => {
                    const isActive = item.client_reaction === key;
                    return (
                      <button
                        key={key}
                        onClick={() => onReaction(item.id, key)}
                        disabled={reactingTo === item.id}
                        data-testid={`panel-reaction-${key}`}
                        className={`flex flex-col items-center gap-1.5 py-3 px-2 text-xs transition border ${
                          isActive
                            ? activeClass
                            : 'border-[#222] text-[#888] hover:text-white hover:border-[#444]'
                        }`}
                      >
                        <Icon className={`w-4 h-4 ${isActive && key === 'love_it' ? 'fill-current' : ''}`} />
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Client Notes */}
              <div className="border-t border-[#FFFFFF] pt-5">
                <p className="text-xs text-[#666] uppercase tracking-wider mb-3">Your notes</p>
                <div className="relative">
                  <textarea
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Add your thoughts, questions, or requirements for this property..."
                    rows={3}
                    maxLength={2000}
                    className="w-full bg-[#F9F6EF] border border-[#222] px-4 py-3 pr-12 text-white text-sm placeholder-[#555] focus:border-[#2CA8A0] focus:outline-none resize-none"
                    data-testid="panel-note-input"
                  />
                  <button
                    onClick={() => onSaveNote(item.id, noteText)}
                    disabled={savingNote || noteText === (item.client_note || '')}
                    className="absolute bottom-3 right-3 p-1.5 text-[#2CA8A0] hover:bg-[#2CA8A0]/10 transition disabled:text-[#333] disabled:hover:bg-transparent"
                    data-testid="panel-save-note-btn"
                  >
                    {savingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </div>
                {item.client_note && item.client_note === noteText && (
                  <p className="mt-1.5 text-xs text-[#555] flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Saved
                  </p>
                )}
              </div>
            </div>
          </ScrollArea>
        )}
      </SheetContent>
    </Sheet>
  );
};

const PortalShortlist = () => {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reactingTo, setReactingTo] = useState(null);
  const [savingNote, setSavingNote] = useState(false);
  const [selectedItemIndex, setSelectedItemIndex] = useState(null);

  useEffect(() => {
    if (user) fetchShortlist();
  }, [user]);

  const fetchShortlist = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/portal/shortlist`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setItems(data.items || []);
      }
    } catch (err) {
      console.error('Error fetching shortlist:', err);
    } finally {
      setLoading(false);
    }
  };

  const markViewed = useCallback(async (itemId) => {
    try {
      await fetch(`${API_URL}/api/portal/shortlist/${itemId}/view`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (err) { /* silent */ }
  }, []);

  const handleSelectItem = useCallback((index) => {
    setSelectedItemIndex(index);
    const item = items[index];
    if (item) markViewed(item.id);
  }, [items, markViewed]);

  const handleReaction = async (itemId, action) => {
    setReactingTo(itemId);
    try {
      const response = await fetch(`${API_URL}/api/portal/shortlist/${itemId}/react`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action })
      });
      if (response.ok) {
        setItems(prev => prev.map(item =>
          item.id === itemId ? { ...item, client_reaction: action } : item
        ));
      }
    } catch (err) {
      console.error('Error reacting:', err);
    } finally {
      setReactingTo(null);
    }
  };

  const handleSaveNote = async (itemId, note) => {
    setSavingNote(true);
    try {
      const response = await fetch(`${API_URL}/api/portal/shortlist/${itemId}/note`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ note })
      });
      if (response.ok) {
        setItems(prev => prev.map(item =>
          item.id === itemId ? { ...item, client_note: note } : item
        ));
      }
    } catch (err) {
      console.error('Error saving note:', err);
    } finally {
      setSavingNote(false);
    }
  };

  const handleNavigate = (direction) => {
    const newIndex = direction === 'next' ? selectedItemIndex + 1 : selectedItemIndex - 1;
    if (newIndex >= 0 && newIndex < items.length) {
      handleSelectItem(newIndex);
    }
  };

  const getReactionBadge = (reaction) => {
    const r = REACTIONS.find(rx => rx.key === reaction);
    if (!r) return null;
    return (
      <span className={`px-2 py-0.5 text-xs border ${r.activeClass}`}>{r.label}</span>
    );
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#F9F6EF] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#2CA8A0] animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/portal" replace />;

  return (
    <PortalLayout activeTab="shortlist">
      <div className="p-4 sm:p-6 lg:p-12">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-light text-white mb-2">Your Property Shortlist</h1>
          <p className="text-[#888] text-sm sm:text-base">
            Properties curated by your advisory team. Click to view details, leave notes, and share your feedback.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#2CA8A0] animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-state">
            <Home className="w-16 h-16 text-[#333] mx-auto mb-6" />
            <h2 className="text-xl text-white mb-2">No properties yet</h2>
            <p className="text-[#888] max-w-md mx-auto">
              Your advisory team will add property suggestions here based on your requirements.
              Check back soon or contact your advisor.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {items.map((item, index) => (
              <div
                key={item.id}
                onClick={() => handleSelectItem(index)}
                className={`bg-[#F9F6EF] border transition-all cursor-pointer group ${
                  item.client_reaction === 'not_for_me'
                    ? 'border-[#FFFFFF] opacity-50 hover:opacity-75'
                    : 'border-[#FFFFFF] hover:border-[#333]'
                }`}
                data-testid={`shortlist-item-${item.id}`}
              >
                {/* Image */}
                <div className="aspect-[16/10] bg-[#111] relative overflow-hidden">
                  <ListingImage
                    src={item.images?.[0]}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
                  />
                  <div className="absolute top-3 left-3 flex gap-2">
                    {item.client_reaction && getReactionBadge(item.client_reaction)}
                  </div>
                  <span className={`absolute top-3 right-3 px-2 py-1 text-xs font-medium ${
                    item.intent === 'rent' ? 'bg-blue-500 text-white' : 'bg-green-500 text-white'
                  }`}>
                    {item.intent === 'rent' ? 'Rent' : 'Buy'}
                  </span>
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <div className="bg-black/70 px-4 py-2 flex items-center gap-2 text-white text-sm">
                      <Eye className="w-4 h-4" />
                      View Details
                    </div>
                  </div>
                  {item.images?.length > 1 && (
                    <div className="absolute bottom-2 right-2 bg-black/70 px-1.5 py-0.5 text-xs text-white">
                      +{item.images.length - 1}
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="text-white font-medium line-clamp-1">{item.title}</h3>
                    <p className="text-[#2CA8A0] font-medium shrink-0 text-sm">
                      {formatPrice(item.price_amount, item.price_currency, item.intent)}
                    </p>
                  </div>
                  {(item.neighborhood || item.city) && (
                    <div className="flex items-center gap-1.5 text-[#888] text-sm mb-2">
                      <MapPin className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">{typeof item.neighborhood === 'object' ? (item.neighborhood?.name || '') : item.neighborhood ? `${item.neighborhood}, ` : ''}{typeof item.city === 'object' ? (item.city?.name || '') : item.city}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-3 text-[#666] text-xs">
                    {item.bedrooms && <span className="flex items-center gap-1"><Bed className="w-3.5 h-3.5" />{item.bedrooms} bed</span>}
                    {item.bathrooms && <span className="flex items-center gap-1"><Bath className="w-3.5 h-3.5" />{item.bathrooms} bath</span>}
                    {item.area_sqm && <span className="flex items-center gap-1"><Square className="w-3.5 h-3.5" />{item.area_sqm} m²</span>}
                  </div>
                  {item.client_note && (
                    <p className="mt-2 text-xs text-[#666] truncate flex items-center gap-1">
                      <MessageSquare className="w-3 h-3 shrink-0" /> {item.client_note}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ListingDetailPanel
        open={selectedItemIndex !== null && !!items[selectedItemIndex]}
        item={selectedItemIndex !== null ? items[selectedItemIndex] : null}
        items={items}
        currentIndex={selectedItemIndex ?? 0}
        onClose={() => setSelectedItemIndex(null)}
        onNavigate={handleNavigate}
        onReaction={handleReaction}
        onSaveNote={handleSaveNote}
        reactingTo={reactingTo}
        savingNote={savingNote}
      />
    </PortalLayout>
  );
};

export default PortalShortlist;
